/**
 * Billing & Analytics — formatting, plan fallbacks and the insight rules.
 *
 * The page renders numbers that stand in for money, so the formatting lives in
 * one place: a page count and a page *cost* must never be rounded by two
 * different rules on two different cards.
 */
import { percentParts } from './apportion'

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

/**
 * Compact form for headline tiles — 12.4K, 3.1M, 1.2B. Exact below 1,000.
 *
 * A figure that rounds up to the next unit is shown in that unit: 999,950 is
 * "1.0M", never "1000.0K".
 */
export function formatCompact(num) {
  const n = Number(num) || 0
  const abs = Math.abs(n)
  const scaled = (divisor, suffix) => `${(n / divisor).toFixed(1)}${suffix}`
  const rounded = (divisor) => Math.abs(Number((n / divisor).toFixed(1)))

  if (abs >= 1_000_000_000) return scaled(1_000_000_000, 'B')
  if (abs >= 1_000_000) return rounded(1_000_000) >= 1000 ? scaled(1_000_000_000, 'B') : scaled(1_000_000, 'M')
  if (abs >= 1_000) return rounded(1_000) >= 1000 ? scaled(1_000_000, 'M') : scaled(1_000, 'K')
  return n.toLocaleString()
}

/** Exact form — used anywhere the figure is billable and must reconcile. */
export function formatInt(num) {
  return (Number(num) || 0).toLocaleString()
}

// en-GB renders USD as "US$" to disambiguate from GBP — picking the locale
// per currency instead gets the plain "$"/"£" every other surface expects.
const CURRENCY_LOCALES = { USD: 'en-US', GBP: 'en-GB' }

/** Plan money. Whole pounds unless the pence carry meaning (overage does). */
export function formatMoney(value, currency = { code: 'GBP', symbol: '£' }, { pence = true } = {}) {
  const n = Number(value) || 0
  const code = currency.code || 'GBP'
  try {
    return new Intl.NumberFormat(CURRENCY_LOCALES[code] || 'en-GB', {
      style: 'currency',
      currency: code,
      minimumFractionDigits: pence ? 2 : 0,
      maximumFractionDigits: pence ? 2 : 0,
    }).format(n)
  } catch {
    return `${currency.symbol || '£'}${n.toFixed(pence ? 2 : 0)}`
  }
}

/** Plan money at sub-penny precision — unit rates, not totals. */
export function formatMoneyPrecise(value, currency = { code: 'GBP', symbol: '\u00a3' }, digits = 3) {
  const n = Number(value) || 0
  const code = currency.code || 'GBP'
  try {
    return new Intl.NumberFormat(CURRENCY_LOCALES[code] || 'en-GB', {
      style: 'currency',
      currency: code,
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(n)
  } catch {
    return `${currency.symbol || '\u00a3'}${n.toFixed(digits)}`
  }
}

/**
 * What the Azure estimate on this page is, in words that hold for a Marketplace
 * install (the customer's own subscription) and a Synapx-hosted one alike.
 */
export const AI_CHARGES_HELP = "Estimated charges for AI document processing, at Microsoft's published rates. "
  + 'Billed by Microsoft to the Azure subscription that runs this app, separate from your plan. '
  + 'Excludes hosting (servers, database, storage).'

/** Azure consumption estimates are published in USD, so they stay in USD. */
export function formatUsd(value, digits = 2) {
  return `$${(Number(value) || 0).toFixed(digits)}`
}

// ---------------------------------------------------------------------------
// Display currency — a whole-page USD ⇄ GBP switch
// ---------------------------------------------------------------------------
//
// Every money figure on this page natively lives in one of two currencies:
// the plan fee (GBP, the actual Azure Marketplace contract) or the Azure
// consumption estimate (USD, what Microsoft publishes its rates in). Neither
// is ever recalculated server-side in the other currency, so converting one
// into the other for display is always an approximation at a fixed rate —
// never the invoiced or metered figure. There is no live FX feed here.
//
// So the switch only converts the Azure estimates, and marks every converted
// figure with "≈". Plan money (fee, overage price, projected bill, effective
// rate, budgets) always stays in the plan's own currency: "$2,532 / month" is
// a price nobody sells.
export const CURRENCIES = {
  USD: { code: 'USD', symbol: '$' },
  GBP: { code: 'GBP', symbol: '£' },
}

export const USD_GBP_RATE = 0.79 // review periodically; ~ long-run GBP/USD of 1.27

/** A fixed-rate conversion between the two currencies the page shows. */
export function convertAmount(value, fromCode, toCode) {
  const n = Number(value) || 0
  if (!toCode || toCode === fromCode) return n
  if (fromCode === 'USD' && toCode === 'GBP') return n * USD_GBP_RATE
  if (fromCode === 'GBP' && toCode === 'USD') return n / USD_GBP_RATE
  return n
}

/**
 * Plan money, always in the plan's own currency. `_displayCode` is the page's
 * display currency, accepted so call sites read the same as the Azure
 * formatters, and deliberately not applied: plan prices are never converted.
 */
export function formatMoneyAs(value, nativeCurrency, _displayCode, opts = {}) {
  return formatMoney(value, nativeCurrency || CURRENCIES.GBP, opts)
}

/** Sub-penny precision version of `formatMoneyAs`, for unit rates. Never converted either. */
export function formatMoneyPreciseAs(value, nativeCurrency, _displayCode, digits = 3) {
  return formatMoneyPrecise(value, nativeCurrency || CURRENCIES.GBP, digits)
}

/**
 * A USD-native estimate, converted and formatted as `displayCode` (defaults to
 * USD). A converted figure is marked "≈": it is a fixed-rate approximation.
 */
export function formatUsdAs(value, displayCode, digits = 2) {
  if ((displayCode || 'USD') === 'USD') return formatUsd(value, digits)
  return `≈${formatMoneyPrecise(convertAmount(value, 'USD', 'GBP'), CURRENCIES.GBP, digits)}`
}

/** Compact USD-native figure for chart axes — "$1.2k", "£45", "$0.80". */
export function formatUsdShort(value, displayCode) {
  const code = displayCode === 'GBP' ? 'GBP' : 'USD'
  const n = convertAmount(value, 'USD', code)
  const symbol = CURRENCIES[code].symbol
  if (Math.abs(n) >= 1000) return `${symbol}${(n / 1000).toFixed(Math.abs(n) >= 10_000 ? 0 : 1)}k`
  if (Math.abs(n) >= 10 || n === 0) return `${symbol}${Math.round(n)}`
  return `${symbol}${n.toFixed(2)}`
}

const DISPLAY_CURRENCY_KEY = 'xtractai-billing-display-currency'

/** Read the stored display-currency preference, defaulting to GBP (the currency the plan is actually billed in). */
export function getStoredDisplayCurrency() {
  try {
    const stored = localStorage.getItem(DISPLAY_CURRENCY_KEY)
    return stored === 'USD' ? 'USD' : 'GBP'
  } catch {
    return 'GBP'
  }
}

export function storeDisplayCurrency(value) {
  try {
    localStorage.setItem(DISPLAY_CURRENCY_KEY, value === 'USD' ? 'USD' : 'GBP')
  } catch {
    // Non-fatal: the toggle still works for this session.
  }
}

export function formatBytes(bytes) {
  const n = Number(bytes) || 0
  if (n >= 1024 ** 3) return `${(n / 1024 ** 3).toFixed(2)} GB`
  if (n >= 1024 ** 2) return `${(n / 1024 ** 2).toFixed(1)} MB`
  if (n >= 1024) return `${(n / 1024).toFixed(0)} KB`
  return `${n} B`
}

/** "2026-09" -> "Sep 26"; "2026-W13" -> "W13". */
export function formatPeriodLabel(period) {
  if (!period) return ''
  if (period.includes('W')) return period.split('-')[1]
  const [year, month] = period.split('-')
  return new Date(Number(year), Number(month) - 1).toLocaleDateString('en-GB', {
    month: 'short',
    year: '2-digit',
  })
}

/** Percentage change, or null when there is no comparable prior period. */
export function percentChange(current, previous) {
  if (previous === null || previous === undefined || previous === 0) return null
  return ((current - previous) / previous) * 100
}

// ---------------------------------------------------------------------------
// Plan
// ---------------------------------------------------------------------------

const CURRENCY = { code: 'GBP', symbol: '£' }

/**
 * The plan block from the summary endpoint. The backend is the only source:
 * it knows which plan this deployment was sold (BILLING_PLAN). When it sends
 * no plan, the page shows "no plan set up" rather than inventing one from a
 * price list here.
 */
export function resolvePlan(summary) {
  const raw = summary?.plan
  if (raw && typeof raw === 'object') return raw
  return {
    configured: false,
    id: null,
    name: null,
    currency: CURRENCY,
    cycle: null,
    usage: { pages_used: Math.max(0, Number(summary?.current_month?.pages_processed) || 0) },
    catalogue: [],
    recommendation: null,
  }
}

const DAY_MS = 24 * 60 * 60 * 1000

/** "14 Oct – 13 Nov 2026": the billing month, end shown inclusive. */
export function formatBillingMonth(cycle) {
  const start = new Date(cycle?.start)
  const end = new Date(new Date(cycle?.end).getTime() - DAY_MS)
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return ''
  const day = (d, withYear) => d.toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', ...(withYear ? { year: 'numeric' } : {}), timeZone: 'UTC',
  })
  return `${day(start, start.getUTCFullYear() !== end.getUTCFullYear())} – ${day(end, true)}`
}

/** Calendar-month label — "September 2026". For an anchored cycle use formatCycleLabel. */
export function formatCycle(cycle) {
  if (!cycle?.start) return ''
  const d = new Date(cycle.start)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' })
}

/**
 * The label for a billing month, always as its date range (end inclusive, in
 * UTC): "14 Oct – 13 Nov 2026" when it is anchored to a Marketplace purchase
 * date, "1–31 Oct 2026" for a calendar month. A month name alone would read
 * "September 2026" for a billing month that has rolled into October, and would
 * hide where a calendar month starts and ends.
 */
export function formatCycleLabel(cycle) {
  const start = new Date(cycle?.start)
  const end = new Date(new Date(cycle?.end).getTime() - DAY_MS)
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return ''
  if (start.getUTCFullYear() === end.getUTCFullYear() && start.getUTCMonth() === end.getUTCMonth()) {
    const monthYear = end.toLocaleDateString('en-GB', { month: 'short', year: 'numeric', timeZone: 'UTC' })
    return `${start.getUTCDate()}–${end.getUTCDate()} ${monthYear}`
  }
  return formatBillingMonth(cycle)
}

/** Tooltip for a billing month's date range. */
export function billingMonthTooltip(cycle) {
  return cycle?.anchored
    ? 'Times are UTC. Billing months run from your Microsoft Marketplace purchase date.'
    : 'Times are UTC. Billing months follow the calendar month.'
}

/** "13 days left", "1 day left". */
export function formatDaysLeft(days) {
  const n = Number(days) || 0
  return `${n} ${n === 1 ? 'day' : 'days'} left`
}

// ---------------------------------------------------------------------------
// Insights
// ---------------------------------------------------------------------------

/**
 * The "so what" line above the charts.
 *
 * Deliberately a small set of rules rather than everything that could be said:
 * an admin who is shown six insights reads none. Each one states a fact, what
 * it means for the bill, and — where there is one — where to go next. Ordered
 * by tone so the thing that costs money is never below the trivia.
 */
export function buildInsights({ plan, pages, prevPages, people, tokens, displayCurrency, totalRequests = null }) {
  const insights = []
  const usage = plan?.usage || {}
  const currency = plan?.currency || CURRENCY
  const fmt = (value, opts) => formatMoneyAs(value, currency, displayCurrency, opts)

  const projected = usage.projected_pages || 0
  const included = usage.pages_included || 0
  const projectedPct = usage.projected_percent || 0
  // The first days of a cycle are too few to extrapolate from: one big upload
  // on day 1 "projects" to a month of the same. Only an explicit false counts
  // (an older backend sends no flag and its projection is taken at face value).
  const reliable = usage.projection_reliable !== false
  // Overage priced at 0 (a private plan with its fee and overage overridden):
  // passing the allowance costs nothing, so it is not raised as an overage.
  const freeOverage = plan?.overage_per_page != null && Number(plan.overage_per_page) === 0

  if (included > 0 && !reliable) {
    if (usage.pages_used > 0) {
      insights.push({
        id: 'early-cycle',
        tone: 'neutral',
        title: 'Too early in the billing month to project',
        body: `${formatInt(usage.pages_used)} of ${formatInt(included)} included pages used so far. A run rate needs at least ${usage.projection_min_days ?? 3} days of this billing month before it is worth reading.`,
      })
    }
  } else if (included > 0 && usage.projected_overage_pages > 0 && freeOverage) {
    insights.push({
      id: 'over-allowance-free',
      tone: 'neutral',
      title: `On track to pass your allowance by ${formatInt(usage.projected_overage_pages)} pages`,
      body: `At the current run rate this billing month ends near ${formatInt(projected)} pages against ${formatInt(included)} included. Your plan has no charge for pages beyond the allowance.`,
    })
  } else if (included > 0 && usage.projected_overage_pages > 0) {
    insights.push({
      id: 'overage',
      tone: 'critical',
      title: `On track to exceed your allowance by ${formatInt(usage.projected_overage_pages)} pages`,
      body: `At the current run rate this billing month ends near ${formatInt(projected)} pages against ${formatInt(included)} included, adding about ${fmt(usage.projected_overage_cost)} of overage to a ${fmt(plan.monthly_fee, { pence: false })} base fee.`,
      action: plan?.recommendation
        ? `${plan.recommendation.plan_name} would save ${fmt(plan.recommendation.monthly_saving)}/month`
        : 'Compare plans',
      actionKey: 'compare',
    })
  } else if (included > 0 && projectedPct >= 85) {
    insights.push({
      id: 'near-limit',
      tone: 'warning',
      title: `Projected to finish the billing month at ${Math.round(projectedPct)}% of your allowance`,
      body: `About ${formatInt(Math.max(0, included - projected))} pages of headroom left at the current rate, with ${plan?.cycle?.days_remaining ?? 0} days to go.`,
      action: 'Compare plans',
      actionKey: 'compare',
    })
  } else if (included > 0 && usage.pages_used > 0) {
    insights.push({
      id: 'within-allowance',
      tone: 'good',
      title: 'Comfortably inside your page allowance',
      body: `Projected ${formatInt(projected)} of ${formatInt(included)} included pages. No overage expected this billing month.`,
    })
  }

  // `prevPages` is the previous billing cycle cut at the same point in the
  // cycle (summary.previous_cycle), so both sides cover the same span. Null
  // when there is no previous cycle: no comparison rather than one against 0.
  const change = percentChange(pages, prevPages)
  if (change !== null && Math.abs(change) >= 20) {
    const up = change > 0
    insights.push({
      id: 'trend',
      tone: up ? 'warning' : 'neutral',
      title: `Page volume is ${up ? 'up' : 'down'} ${Math.abs(Math.round(change))}% on the same point last billing month`,
      body: `${formatInt(pages)} pages so far this billing month against ${formatInt(prevPages)} at the same point in the previous billing month.`,
    })
  }

  // Share of everyone's requests, not of the ten people listed: the list is
  // only the top of the table, so summing it would overstate the top person.
  const listedRequests = (people || []).reduce((sum, u) => sum + (u.requests || 0), 0)
  const requestTotal = totalRequests > 0 ? totalRequests : listedRequests
  if (people?.length > 2 && requestTotal > 0) {
    const top = people[0]
    const share = (top.requests / requestTotal) * 100
    if (share >= 50) {
      insights.push({
        id: 'concentration',
        tone: 'neutral',
        title: `${top.username} accounts for ${Math.round(share)}% of this billing month's requests`,
        body: 'Volume concentrated on one account is usually a shared mailbox or an automation, worth confirming before it is read as a single person’s workload.',
        action: 'View people',
        actionKey: 'people',
      })
    }
  }

  if (!usage.pages_used && !tokens) {
    // Only a billable plan with a fee has one that "still applies".
    const feeApplies = plan?.billable !== false && Number(plan?.monthly_fee) > 0
    return [{
      id: 'idle',
      tone: 'neutral',
      title: 'No usage recorded this billing month yet',
      body: `${feeApplies ? 'Your plan fee still applies. ' : ''}Figures here update as soon as the first document is processed.`,
    }]
  }

  return insights.slice(0, 3)
}

// ---------------------------------------------------------------------------
// Unit economics
// ---------------------------------------------------------------------------

/**
 * What one unit of work costs.
 *
 * Totals answer "how much"; unit costs answer "is this getting better or
 * worse", which is the question that survives a month where volume doubled.
 * Two currencies sit side by side on purpose and are labelled as such: the
 * Azure consumption estimate is published in USD, while what you actually pay
 * is the plan, in pounds.
 */
export function unitEconomics({ summary, plan, displayCurrency }) {
  const volumes = summary?.current_month || {}
  const consumption = summary?.estimated_costs?.current_month || {}
  const spend = consumption.total_estimated_usd || 0
  const usage = plan?.usage || {}
  const currency = plan?.currency || { code: 'GBP', symbol: '£' }

  const per = (value, count) => (count > 0 ? value / count : null)
  // Internal and contract plans have no Marketplace price, so there is no rate
  // to work out: the card says what the plan is instead of a dash, and only
  // says "no activity" when no page has been processed.
  const unpriced = plan?.billable === false || usage.projected_total_cost == null
  const pagesUsed = Number(usage.pages_used) || 0

  return [
    {
      key: 'per_request',
      label: 'Azure cost per request',
      value: per(spend, volumes.requests),
      format: v => formatUsdAs(v, displayCurrency, 3),
      foot: `${formatInt(volumes.requests)} requests this billing month`,
      help: 'Estimated AI processing charges for the billing month divided by the requests raised in it. Track the direction, not the decimal: a rising figure means each request is doing more AI work.',
    },
    {
      key: 'per_document',
      label: 'Azure cost per document',
      value: per(spend, volumes.documents_processed),
      format: v => formatUsdAs(v, displayCurrency, 3),
      foot: `${formatInt(volumes.documents_processed)} documents this billing month`,
      help: 'Same consumption estimate, divided by documents. A long document costs more than a short one, so this moves with the mix of what you send.',
    },
    {
      key: 'per_page',
      label: 'Azure cost per page',
      value: per(spend, volumes.pages_processed),
      format: v => formatUsdAs(v, displayCurrency, 4),
      foot: `${formatInt(volumes.pages_processed)} pages read`,
      help: 'The most stable of the three: pages are what Azure Content Understanding actually charges for.',
    },
    {
      key: 'plan_per_page',
      label: 'Your effective rate per page',
      // No Marketplace price on internal / contract plans: say so, never £0.
      value: !unpriced && usage.projected_pages > 0
        ? usage.projected_total_cost / usage.projected_pages : null,
      // Shown in place of a figure when the plan has no per-page price.
      text: unpriced ? (plan?.configured === false ? 'No plan' : (plan?.price_label || 'Not billed')) : null,
      format: v => formatMoneyPreciseAs(v, currency, displayCurrency, 3),
      foot: unpriced
        ? (pagesUsed > 0 ? `${formatInt(pagesUsed)} pages this billing month` : null)
        : `${formatMoneyAs(usage.projected_total_cost, currency, displayCurrency, { pence: false })} over ${formatInt(usage.projected_pages)} projected pages`,
      help: unpriced
        ? 'This plan has no per-page price: pages are not billed through Microsoft Marketplace, so there is no rate to work out.'
        : 'What a page really costs you on this plan: the base fee plus projected overage, spread across projected volume. It falls as you use more of an allowance you are paying for either way.',
      primary: true,
    },
  ]
}

/**
 * Cost drivers — what the consumption estimate was actually spent on.
 *
 * Infrastructure is included, flagged as fixed, only when the summary carries
 * a figure for it: it is a flat monthly cost rather than a share of this
 * cycle's consumption. The backend no longer estimates it (the old hardcoded
 * table matched no real deployment), and a $0 "Platform infrastructure" row
 * would read as a measurement that says it costs nothing.
 */
export function costDrivers({ summary, chart, scope = 'current_month' }) {
  const consumption = summary?.estimated_costs?.[scope] || {}
  const infra = summary?.infrastructure_costs

  const drivers = [
    {
      id: 'documents',
      name: 'Document processing',
      plain: 'Reading pages with Azure Content Understanding',
      cost: consumption.document_processing_usd || 0,
      unit: `${formatInt(consumption.pages_billed)} pages`,
      color: chart.series[0],
    },
    {
      id: 'ai',
      name: 'AI inference',
      plain: 'Normalising fields, reading email bodies, LLM fallback',
      cost: consumption.ai_inference_usd || 0,
      unit: `${formatCompact(consumption.total_tokens)} tokens`,
      color: chart.series[2],
    },
  ]
  if (infra && typeof infra === 'object') {
    drivers.push({
      id: 'infra',
      name: 'Platform infrastructure',
      plain: 'Fixed monthly cost of the Azure resources behind Xtract',
      cost: infra.monthly_total_usd || 0,
      unit: 'flat monthly',
      color: chart.series[1],
      fixed: true,
    })
  }
  return drivers
}

/**
 * Spread a total across items in proportion to a count.
 *
 * Used to put a cost against each template. Nothing in the data model records
 * consumption per template — jobs carry tokens and pages, not the template they
 * ran for — so this is an allocation, not a measurement, and every surface that
 * shows it says so.
 */
export function allocateByShare(items, total, countKey = 'total') {
  const sum = items.reduce((acc, item) => acc + (item[countKey] || 0), 0)
  // Whole-number shares that add up to exactly 100, for display. `share` stays
  // the exact fraction the allocation is computed from.
  const pct = percentParts(items.map(item => item[countKey] || 0))
  return items.map((item, i) => {
    const share = sum > 0 ? (item[countKey] || 0) / sum : 0
    return { ...item, share, sharePct: pct[i], allocated: total * share }
  })
}

/** Hand a generated file to the browser. */
export function downloadCsv(filename, content) {
  const blob = content instanceof Blob ? content : new Blob([content], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

/**
 * Collapse a long daily series into fewer, wider buckets.
 *
 * Ninety daily points squeezed into a 120px sparkline is noise: every cell
 * reads as a spike and the shape — which is the only thing a sparkline is for
 * — disappears. Bucketing sums whole days together so the trend survives, and
 * the returned `size` lets the caller relabel ("per week", not "per day")
 * rather than quietly changing what the marks mean.
 */
export function bucketSeries(values, maxPoints = 24) {
  const list = values || []
  if (list.length <= maxPoints) return { values: list, size: 1 }

  const size = Math.ceil(list.length / maxPoints)
  const buckets = []
  for (let i = 0; i < list.length; i += size) {
    buckets.push(list.slice(i, i + size).reduce((sum, v) => sum + (Number(v) || 0), 0))
  }
  return { values: buckets, size }
}

